import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { BRAND } from '@crew/shared';
import { BrandEmblem, BrandLogoFull } from '@/components/brand/BrandLogo';
import { EyeIcon, EyeOffIcon, ShieldIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Notice } from '@/components/ui/Notice';
import { TextField } from '@/components/ui/TextField';
import { DEMO_CREDENTIALS, IS_DEMO } from '@/demo/demoMode';
import { requestPasswordReset } from '@/services/authService';

type Mode = 'signin' | 'reset';

const LAST_EMAIL_KEY = 'sa-last-email';

export function LoginPage() {
  const { status, signIn } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem(LAST_EMAIL_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [remember, setRemember] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  if (status.kind === 'authorized') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  const verifying = status.kind === 'verifying';
  const notice = status.kind === 'signed_out' ? status.notice : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return setError('יש להזין אימייל');
    setBusy(true);
    try {
      if (mode === 'signin') {
        if (!password) return setError('יש להזין סיסמה');
        await signIn(email, password, remember);
        try {
          if (remember) localStorage.setItem(LAST_EMAIL_KEY, email.trim());
          else localStorage.removeItem(LAST_EMAIL_KEY);
        } catch {
          /* אחסון חסום */
        }
      } else {
        await requestPasswordReset(email);
        setResetSent(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'הפעולה נכשלה');
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setResetSent(false);
  }

  return (
    <div className="grid min-h-dvh md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <main className="flex flex-col justify-center px-6 py-10 sm:px-12">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center md:hidden">
            <BrandLogoFull height={140} />
            <div className="mt-3 h-[3px] w-14 rounded-full bg-accent" aria-hidden="true" />
            <p className="mt-3 text-center text-[15px] font-medium text-ink-2">{BRAND.systemLine}</p>
          </div>

          <h1 className="text-[28px] font-bold leading-tight">{mode === 'signin' ? 'כניסת מנהלים' : 'איפוס סיסמה'}</h1>
          <p className="mt-2 text-[15px] text-ink-2">
            {mode === 'signin'
              ? 'התחברו עם האימייל והסיסמה האישיים שלכם.'
              : 'נשלח קישור לאיפוס הסיסמה לכתובת האימייל של החשבון.'}
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5">
            {IS_DEMO && mode === 'signin' && (
              <div className="rounded-control border border-line-strong bg-steel-soft p-3 text-[14px]">
                <div className="font-semibold">מצב תצוגה — התחברו עם:</div>
                <div className="ltr mt-1 text-start">{DEMO_CREDENTIALS.email}</div>
                <div className="ltr text-start">{DEMO_CREDENTIALS.password}</div>
                <button
                  type="button"
                  onClick={() => {
                    setEmail(DEMO_CREDENTIALS.email);
                    setPassword(DEMO_CREDENTIALS.password);
                  }}
                  className="mt-2 font-semibold text-brand underline"
                >
                  מילוי אוטומטי
                </button>
              </div>
            )}
            {notice && <Notice tone="error">{notice}</Notice>}
            {error && <Notice tone="error">{error}</Notice>}
            {resetSent && (
              <Notice tone="success">אם הכתובת רשומה במערכת, יישלח אליה קישור לאיפוס בדקות הקרובות.</Notice>
            )}

            <TextField
              label="אימייל"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.co.il"
              required
            />

            {mode === 'signin' && (
              <TextField
                label="סיסמה"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                trailing={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'הסתרת הסיסמה' : 'הצגת הסיסמה'}
                    aria-pressed={showPassword}
                    className="flex size-10 items-center justify-center rounded-lg text-ink-3 hover:text-ink"
                  >
                    {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
                  </button>
                }
              />
            )}

            {mode === 'signin' && (
              <label className="flex cursor-pointer items-start gap-2.5 text-[15px]">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-1 size-[18px] accent-[var(--color-brand)]" />
                <span>
                  <span className="font-medium">זכור אותי במכשיר הזה</span>
                  <span className="block text-[13px] text-ink-3">נשארים מחוברים עד שלוחצים "יציאה". במחשב משותף — לבטל את הסימון.</span>
                </span>
              </label>
            )}

            <Button type="submit" block loading={busy || verifying}>
              {verifying ? 'מאמתים הרשאות…' : mode === 'signin' ? 'כניסה' : 'שליחת קישור לאיפוס'}
            </Button>

            <div className="text-center">
              {mode === 'signin' ? (
                <button type="button" onClick={() => switchMode('reset')} className="text-[14px] font-medium text-brand hover:underline">
                  שכחתי סיסמה
                </button>
              ) : (
                <button type="button" onClick={() => switchMode('signin')} className="text-[14px] font-medium text-brand hover:underline">
                  חזרה לכניסה
                </button>
              )}
            </div>
          </form>

          <p className="mt-12 flex items-start gap-2 text-[13px] text-ink-3">
            <ShieldIcon size={16} className="mt-0.5 shrink-0" />
            מערכת ניהול פנימית של {BRAND.name}. הגישה מוגבלת לחשבונות מנהל מורשים בלבד, ואין הרשמה עצמית.
          </p>
        </div>
      </main>

      <aside className="relative hidden overflow-hidden bg-brand md:block" aria-hidden="true">
        {/* תבנית משושים עדינה — הצורה של הסמל */}
        <svg className="absolute inset-0 h-full w-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hex" width="72" height="80" patternUnits="userSpaceOnUse">
              <path d="M36 6 62 21v30L36 66 10 51V21Z" fill="none" stroke="#fff" strokeWidth="1.2" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hex)" />
        </svg>
        <div className="relative flex h-full flex-col items-center justify-center px-12 text-center">
          <BrandEmblem size={220} />
          <div className="mt-8 text-[44px] font-extrabold leading-none tracking-tight text-white">{BRAND.name}</div>
          <div className="mt-3 text-[20px] text-steel">{BRAND.tagline}</div>
          <div className="mt-8 h-[3px] w-24 rounded-full bg-accent" />
          <p className="mt-8 max-w-sm text-[18px] font-medium leading-relaxed text-white/85">{BRAND.systemLine}</p>
        </div>
      </aside>
    </div>
  );
}
